import { MaterialCommunityIcons } from '@expo/vector-icons';
import {
  AssetRoutine,
  AssetRoutineState,
  Issue,
  IssueCreationSource,
  useFindByQuery,
  WorkspaceAsset,
  WorkspaceRoutine,
} from '@gripp/shared-logic';
import { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dimensions,
  Modal,
  Platform,
  Pressable,
  StyleProp,
  StyleSheet,
  Text,
  View,
  ViewStyle,
} from 'react-native';
import { Portal, Surface } from 'react-native-paper';
import { Colors } from '../../../themes';
import { TopModalBar } from '../../modals/topModalBar';
import { AlertListModal } from '../../new-alerts/components/alertListModal';
import { NewAlertModal } from '../../new-alerts/components/newAlertModal';
import { RoutineModule } from '../types';
import { RoutineForm } from './routineForm';

export type RoutineModalProps = {
  workspaceAsset?: WorkspaceAsset;
  module: RoutineModule<any, any>;
  routine?: WorkspaceRoutine;
  state?: AssetRoutineState<any>;
  additionalState?: any;
  visible: boolean;
  sourceIssue?: Issue;
  onCompleted: () => Promise<void>;
  onCanceled: () => void;
  confirmCancel?: boolean;
  preview?: boolean;
  rightFormButton?: ReactNode;
  footer?: ReactNode;
  showPortal?: boolean;
  children?: ReactNode;
  alertsRightButton?: boolean;
  isIssueEditRoutine?: boolean;
  assetRoutine?: AssetRoutine;
  onSaveDraft?: () => void;
  showSaveDraftButton?: boolean;
  draftData?: any;
  customModalDraft?: ReactNode;
  showNativeModal?: boolean;
  showConfirmBack?: boolean;
  createdFrom?: IssueCreationSource;
  productId?: string;
  workspace?: string;
  navigateToAlerts?: (path: string) => void; // Optional navigation function for web
};

const ContainerModal = (props: RoutineModalProps) => {
  if (props.showPortal) {
    return <Portal>{props?.children}</Portal>;
  }
  return (
    <Modal
      visible={props.visible}
      onRequestClose={props.onCanceled}
      transparent={true}
    >
      {props.children}
    </Modal>
  );
};
export const RoutineModal = (props: RoutineModalProps) => {
  const [newAlertModalVisible, setNewAlertModalVisible] = useState(false);
  const [alertListModalVisible, setAlertListModalVisible] = useState(false);

  const [routineModalStyle, setRoutineModalStyle] = useState<
    StyleProp<ViewStyle>
  >(styles.outerContainer);

  const [alertListModalStyle, setAlertListModalStyle] = useState<
    StyleProp<ViewStyle>
  >(styles.hidden);

  const { t } = useTranslation();
  const { routine, workspaceAsset, navigateToAlerts } = props;

  const { data: routineWorkflowData, isLoading: isWorkflowLoading } =
    useFindByQuery({
      modelName: 'workflow',
      query: FIND_WORKFLOWS,
      variables: {
        filter: {
          workspaceAsset: { eq: workspaceAsset?.id },
          json: { 'trigger.config.workspaceRoutineId': routine?.id },
        },
      },
    });

  if (!props.visible) return null;

  const showRoutineModal = () => {
    setRoutineModalStyle(styles.outerContainer);
  };

  const hideRoutinesModal = () => {
    setRoutineModalStyle(styles.hidden);
  };

  const showNewAlertModal = () => {
    setNewAlertModalVisible(true);
  };

  const hideNewAlertModal = () => {
    setNewAlertModalVisible(false);
  };

  const showAlertListModal = () => {
    setAlertListModalVisible(true);
    setAlertListModalStyle(styles.outerContainer);
  };

  const hideAlertListModal = () => {
    setAlertListModalStyle(styles.hidden);
  };

  const closeAlertListModal = () => {
    setAlertListModalVisible(false);
    setAlertListModalStyle(styles.hidden);
  };

  const closeNewAlertModal = () => {
    hideNewAlertModal();
    showAlertListModal();
  };

  const closeEditAlertModal = () => {
    showAlertListModal();
  };

  const onAlertsPress = () => {
    if (!routine?.id) return;

    // On web, navigate to SlideLayout instead of showing modals
    if (Platform.OS === 'web' && navigateToAlerts) {
      if (props.productId) {
        navigateToAlerts(`/products/${props.productId}/alerts/${routine.id}`);
      } else {
        const params = new URLSearchParams();
        if (workspaceAsset?.id)
          params.set('workspaceAssetId', workspaceAsset.id);
        const queryString = params.toString();
        navigateToAlerts(
          `/alerts/${routine.id}${queryString ? `?${queryString}` : ''}`
        );
      }
      props.onCanceled(); // Close the routine modal
      return;
    }

    // On mobile, use modals
    if (!routineWorkflowData) return;
    const workflowCount = Number(routineWorkflowData.count) || 0;
    hideRoutinesModal();
    workflowCount > 0 ? showAlertListModal() : showNewAlertModal();
  };

  const alertButton = (
    <Pressable style={styles.rightButton} onPress={() => onAlertsPress()}>
      <Text style={styles.rightButtonLabel}>{t('alerts.alert')}</Text>
      <MaterialCommunityIcons
        size={20}
        style={{ color: Colors.primaryDark }}
        name="chevron-right"
      />
    </Pressable>
  );

  return (
    <>
      <ContainerModal {...props}>
        <View style={routineModalStyle}>
          <Surface style={styles.innerContainer}>
            <View style={styles.container}>
              {props.workspaceAsset && (
                <RoutineForm
                  workspaceAsset={props.workspaceAsset}
                  state={props.state}
                  additionalState={props.additionalState}
                  module={props.module}
                  routine={props.routine}
                  onCompleted={props.onCompleted}
                  sourceIssue={props.sourceIssue}
                  onCancel={props.onCanceled}
                  topBar={TopModalBar}
                  preview={props.preview}
                  rightButton={
                    props.alertsRightButton
                      ? alertButton
                      : props.rightFormButton
                  }
                  footer={props.footer}
                  isIssueEditRoutine={props.isIssueEditRoutine}
                  assetRoutine={props.assetRoutine}
                  onSaveDraft={props.onSaveDraft}
                  showSaveDraftButton={props.showSaveDraftButton}
                  draftData={props.draftData}
                  showNativeModal={props.showNativeModal}
                  showConfirmBack={props.showConfirmBack}
                  createdFrom={props.createdFrom}
                />
              )}
            </View>
          </Surface>
        </View>
      </ContainerModal>
      {workspaceAsset && routine && (
        <NewAlertModal
          newAlertModalVisible={newAlertModalVisible}
          onClose={closeNewAlertModal}
          workspaceAssetId={workspaceAsset.id}
          routineId={routine.id}
          productId={props.productId}
          workspace={props.workspace}
        />
      )}
      {workspaceAsset && routine && (
        <AlertListModal
          alertListModalVisible={alertListModalVisible}
          alertListModalStyle={alertListModalStyle}
          workspaceAssetId={workspaceAsset.id}
          routineId={routine.id}
          onClose={() => {
            closeAlertListModal();
            showRoutineModal();
          }}
          closeNewAlertModal={closeNewAlertModal}
          closeEditAlertModal={closeEditAlertModal}
          newAlertModalVisible={newAlertModalVisible}
          showNewAlertModal={showNewAlertModal}
          closeAlertListModal={closeAlertListModal}
          hideAlertListModal={hideAlertListModal}
          productId={props.productId}
        />
      )}
      {props.customModalDraft && props.customModalDraft}
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.primaryGrayBackground,
    borderRadius: 12,
    paddingBottom: 12,
  },
  hidden: {
    height: 0,
    width: 0,
    maxHeight: 0,
    minHeight: 0,
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  innerContainer: {
    width: '100%',
    maxWidth: 560,
    minHeight: 461,
    maxHeight: Dimensions.get('window').height * 0.8,
    backgroundColor: Colors.primaryGrayBackground,
    borderRadius: 12,
    overflow: 'hidden',
    elevation: 10,
  },
  outerContainer: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: Colors.blackInactive + 'AA', // semi-transparent background
    zIndex: 10,
  },
  rightButton: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    columnGap: 2,
    marginRight: 12,
  },
  rightButtonLabel: {
    fontSize: 16,
    fontWeight: '500',
    color: Colors.primaryDark,
  },
});

const FIND_WORKFLOWS = `
  query FindWorkflows($filter: WorkflowFilter) {
    findWorkflows(filter: $filter) {
      count
      items {
        id
        name
        workspace {
          id
        }
        trigger {
          type
          config
        }
        action {
          type
          config
        }
        disabled
        disabledBetween {
          start
          end
        }
        createdBy {
          id
        }
      }
    }
}  

`;
